import { Button } from '@/components/ui/button';
import { useConvexAction } from '@convex-dev/react-query';
import { Player } from '@lottiefiles/react-lottie-player';
import { useMutation } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Navigate,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '~/convex/_generated/api';
import type { ReportContextType } from './layout';

export const ReportProcessing = () => {
  const { t } = useTranslation();
  const { report } = useOutletContext<ReportContextType>();
  const location = useLocation();
  const navigate = useNavigate();

  // Re-run the full generation pipeline from extraction so a retry recovers
  // from either an extraction or a document-export failure.
  const runExtractData = useConvexAction(api.reportActions.extractData);
  const { mutate: retry, isPending: isRetrying } = useMutation({
    mutationFn: runExtractData,
    onError: () => {
      toast.error(t('errors:errorGeneratingReport'));
    },
  });
  const justTriggered = (location.state as { generating?: boolean })
    ?.generating;

  // Once the real status catches up to a processing state, clear the route flag
  // so the completed→edit redirect works when generation finishes.
  useEffect(() => {
    if (
      justTriggered &&
      (report.status === 'generating' ||
        report.status === 'transcribing' ||
        report.status === 'processing')
    ) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [justTriggered, report.status, navigate, location.pathname]);

  if (report.status === 'completed' && !justTriggered) {
    return <Navigate to={`/reports/${report._id}/edit`} replace />;
  }

  if (
    (report.status === 'draft' || report.status === 'recording') &&
    !justTriggered
  ) {
    return <Navigate to={`/reports/${report._id}/record`} replace />;
  }

  if (report.status === 'error') {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center px-6 text-center">
        <div className="mb-3 text-2xl font-bold">
          {t('report:errors.processingFailed')}
        </div>
        <div className="max-w-md text-gray-500">
          {report.errorMessage ??
            t('report:errors.processingFailedDescription')}
        </div>
        <div className="mt-2 max-w-md text-gray-500">
          {t('report:errors.tryAgainLater')}
        </div>
        <Button
          className="mt-6"
          loading={isRetrying}
          disabled={isRetrying}
          onClick={() => retry({ reportId: report._id })}>
          <RefreshCw className="size-4" />
          {t('report:errors.retry')}
        </Button>
      </div>
    );
  }

  const lottieSrc =
    report.status === 'transcribing'
      ? '/transcribing.json'
      : '/generating-document.json';
  const title =
    report.status === 'transcribing'
      ? t('home:transcribingData')
      : t('home:extractingData');
  const description =
    report.status === 'transcribing'
      ? t('home:transcribingDataDescription')
      : t('home:extractingDataDescription');

  return (
    <div className="flex h-full w-full flex-col items-center justify-center">
      <div className="size-64">
        <Player autoplay loop src={lottieSrc} />
      </div>
      <div className="mb-4 text-2xl font-bold">{title}</div>
      <div className="text-gray-500">{description}</div>
    </div>
  );
};
